import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchClients,
  createClient,
  updateClient,
  deleteClient,
} from '../../features/Users/userSlice';
import { Table, Button, Modal, Form, Input, Select, message, Space } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

const Clients = () => {
  const dispatch = useDispatch();
  const { clients, loading } = useSelector((state) => state.users);
  
  // Local states for filtering, modal visibility and form
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [form] = Form.useForm();
  const [formErrors, setFormErrors] = useState({});

  // Local pagination state; initial values in case Redux state is not ready.
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Fetch clients when component mounts or when pagination/status filter changes.
  useEffect(() => {
    dispatch(fetchClients({ page: pagination.current, per_page: pagination.pageSize, status: statusFilter }));
  }, [dispatch, pagination.current, pagination.pageSize, statusFilter]);

  // Updated handleTableChange: receives page and pageSize as parameters.
  const handleTableChange = (page, pageSize) => {
    setPagination({ current: page, pageSize });
    dispatch(fetchClients({ page, per_page: pageSize, status: statusFilter }));
  };

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    // Reset page to 1 when filter changes.
    setPagination({ ...pagination, current: 1 });
    dispatch(fetchClients({ page: 1, per_page: pagination.pageSize, status: value }));
  };

  const handleCreate = () => {
    form.resetFields();
    setSelectedClient(null);
    setIsModalVisible(true);
    setFormErrors({});
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    form.setFieldsValue(client);
    setIsModalVisible(true);
    setFormErrors({});
  };

  const handleDelete = (id) => {
    dispatch(deleteClient(id))
      .unwrap()
      .then(() => {
        message.success('Client deleted successfully');
        // Refresh the list after deletion.
        dispatch(fetchClients({ page: pagination.current, per_page: pagination.pageSize, status: statusFilter }));
      })
      .catch(() => {
        message.error('Failed to delete client');
      });
  };

  const handleSubmit = async (values) => {
    try {
      if (selectedClient) {
        // Send only changed fields during update.
        const changedFields = {};
        Object.keys(values).forEach((key) => {
          if (values[key] !== selectedClient[key]) {
            changedFields[key] = values[key];
          }
        });
        await dispatch(updateClient({ id: selectedClient.id, ...changedFields })).unwrap();
        message.success('Client updated successfully');
      } else {
        await dispatch(createClient(values)).unwrap();
        message.success('Client created successfully');
      }
      setIsModalVisible(false);
      // Refresh the list after creation/update.
      dispatch(fetchClients({ page: pagination.current, per_page: pagination.pageSize, status: statusFilter }));
    } catch (error) {
      if (error.errors) {
        setFormErrors(error.errors); // Set form errors from API response.
      } else {
        message.error('An error occurred. Please try again.');
      }
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Business Name', dataIndex: 'business_name', key: 'business_name' },
    
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button type="primary" onClick={handleCreate} style={{ marginBottom: 16 }}>
        Add Client
      </Button>
      <Select
        placeholder="Filter by status"
        onChange={handleFilterChange}
        style={{ width: 200, marginLeft: 16 }}
        allowClear
        value={statusFilter || undefined}
      >
        <Select.Option value="">All</Select.Option>
        <Select.Option value="pending">Pending</Select.Option>
        <Select.Option value="accepted">Accepted</Select.Option>
        <Select.Option value="rejected">Rejected</Select.Option>
      </Select>
      <Table
        dataSource={clients.data} // Data array from Redux state.
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          current: clients.current_page || pagination.current,
          pageSize: clients.per_page || pagination.pageSize,
          total: clients.total || 0,
          showSizeChanger: false,
          onChange: handleTableChange,
        }}
      />

      <Modal
        title={selectedClient ? 'Edit Client' : 'Add Client'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          {/* Name Field */}
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter the name' }]}
            validateStatus={formErrors.name ? 'error' : ''}
            help={formErrors.name ? formErrors.name[0] : ''}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="business_name"
            label="Business Name"
            rules={[{ required: false, message: 'Please enter the business name' }]}
            validateStatus={formErrors.business_name ? 'error' : ''}
            help={formErrors.name ? formErrors.business_name[0] : ''}
          >
            <Input />
          </Form.Item>

          {/* Email Field */}
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter the email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
            validateStatus={formErrors.email ? 'error' : ''}
            help={formErrors.email ? formErrors.email[0] : ''}
          >
            <Input />
          </Form.Item>

          {/* Address Field */}
          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: 'Please enter the address' }]}
            validateStatus={formErrors.address ? 'error' : ''}
            help={formErrors.address ? formErrors.address[0] : ''}
          >
            <Input />
          </Form.Item>

          {/* Phone Field */}
          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: 'Please enter the phone number' }]}
            validateStatus={formErrors.phone ? 'error' : ''}
            help={formErrors.phone ? formErrors.phone[0] : ''}
          >
            <Input />
          </Form.Item>

          {/* Password Fields (Only for creation) */}
          {!selectedClient && (
            <>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Please enter the password' }]}
                validateStatus={formErrors.password ? 'error' : ''}
                help={formErrors.password ? formErrors.password[0] : ''}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                name="password_confirmation"
                label="Confirm Password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm the password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject('The two passwords do not match');
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>
            </>
          )}

          {/* Status Field */}
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select the status' }]}
            validateStatus={formErrors.status ? 'error' : ''}
            help={formErrors.status ? formErrors.status[0] : ''}
          >
            <Select>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="accepted">Accepted</Select.Option>
              <Select.Option value="rejected">Rejected</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              {selectedClient ? 'Update' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Clients;
